import IconSVG from "../../../styles/svg-icons";
import cn from "classnames";
import { constants } from "../../../constants";
import { ImSubscriptionActionBlocker } from "../../amrPipeline/subscription/ImSubscriptionActionBlocker";
import { OnHoverTooltip } from "../../common";
import { TransactionStatus } from "../../../types/amr-pipeline/enums/TransactionStatus";
import {
    Tooltip,
    TooltipPlacement,
    TooltipTriggerType,
} from "../../common/Tooltip";
import { useMemo, useState } from "react";
import { ContextMenuItem } from "../../controls/ContextMenuItem";
import { Transaction } from "../../../types/amr-pipeline/models/Transaction";
import { Currency } from "../../../types/enums/Currency";
import { OriginatingTransactionClass } from "../../../types/amr-pipeline/models/OriginatingTransactionClass";
import { usePortfolios } from "../../../effects/usePortfolios";
import { AmrClass } from "../../../types/amr-pipeline/models/AmrClass";
import { TransactionType } from "../../../types/amr-pipeline/enums/TransactionType";
import { Rating } from "../../../types/enums/Rating";
import { isinCusip } from "../../../types/security/Security";
import { useAppDispatch } from "../../../effects/useAppDispatch";
import { addToPortfolioActions } from "../../../actions/add-to-portfolio.actions";
import { AddToPortfolioPosition } from "../../../types/state/AddToPortfolioState";
import { isRequestSuccess } from "../../../utils";

const AddToPortfolioIcon = () =>
    <IconSVG name="add-to-portfolio" width={16} height={16} />

interface Identifier {
    ticker: string;
}

interface IdentifierList extends Identifier {
    label: string;
}

interface Props {
    wrapperClassName?: string;
    className?: string;
    disabled?: boolean;
    checkSubscription?: boolean;
    transaction: Transaction;
    transactionClass: OriginatingTransactionClass | AmrClass;
    withText?: boolean;
    withEmptyPlaceholder?: boolean;
}
export function AddToPortfolioLink({
    wrapperClassName,
    className,
    disabled = false,
    checkSubscription = false,
    transaction,
    transactionClass,
    withText,
    withEmptyPlaceholder = true,
}: Props) {
    const dispatch = useAppDispatch();

    const [isTooltipVisible, setIsTooltipVisible] = useState(false);

    const { status, currency, type } = transaction;
    const { rating } = transactionClass;
    const tranche =
        type === TransactionType.amr
            ? (transactionClass as AmrClass).class
            : (transactionClass as OriginatingTransactionClass).tranche;

    const ticker144A = tranche?.ticker144A;
    const tickerRegS = tranche?.tickerRegS;
    const tickerAccdInvCertif = tranche?.tickerAccdInvCertif;

    const { items: portfolios, requestState } = usePortfolios(true);

    const identifierList = [
        { ticker: ticker144A, label: "144A" },
        { ticker: tickerRegS, label: "Reg S" },
        {
            ticker: tickerAccdInvCertif,
            label: "Acc’d Inv./Certif.",
        },
    ].filter(c => c.ticker) as IdentifierList[];

    const withButton = !!(
        status === TransactionStatus.Closed &&
        identifierList.length &&
        rating
    );

    const portfolioSecurity = useMemo(() =>
        withButton
            ? portfolios.map(p => p.securities).flat().find(s =>
                (ticker144A && s.ticker.localeCompare(ticker144A, undefined, { sensitivity: 'accent' }) === 0) ||
                (tickerRegS && s.ticker.localeCompare(tickerRegS, undefined, { sensitivity: 'accent' }) === 0) ||
                (tickerAccdInvCertif && s.ticker.localeCompare(tickerAccdInvCertif, undefined, { sensitivity: 'accent' }) === 0))
            : undefined
        , [
            ticker144A,
            tickerRegS,
            tickerAccdInvCertif,
            portfolios,
            withButton,
        ]);

    if (!withButton) {
        return withEmptyPlaceholder ? (
            <span className="action-empty-placeholder">
                {constants.emptyPlaceholder}
            </span>
        ) : null;
    }

    if (disabled || !isRequestSuccess(requestState)) {
        return (
            <button className={cn("btn-link", className)} disabled={disabled}>
                <AddToPortfolioIcon />
            </button>
        );
    }

    const handleVisibilityChange = (isVisible: boolean) =>
        setIsTooltipVisible(isVisible);

    const handleAddToPortfolioClick = (selectedTicker?: string) => {
        const ticker = portfolioSecurity?.ticker || selectedTicker;

        if (!ticker || !tranche) return;

        const isinCusipValue = portfolioSecurity?.isinCusip || isinCusip({
            isin: tranche.isin144A || tranche.isinRegS || tranche.isinAccdInvCertif,
            cusip: tranche.cusip144A || tranche.cusipRegS || tranche.cusipAccdInvCertif,
            currency: currency?.code ? (currency.code as Currency) : Currency.USD
        });

        const positionCurrency = portfolioSecurity?.currency || currency?.code;
        const position: AddToPortfolioPosition = {
            isinCusip: isinCusipValue,
            securityId: portfolioSecurity?.securityId,
            size: portfolioSecurity?.size || 0,
            ticker,
            currency: positionCurrency
                ? (positionCurrency as Currency)
                : Currency.USD,
            rating: (portfolioSecurity?.rating || rating) as Rating,
        };

        dispatch(addToPortfolioActions.setPopupVisibility(true, position));
    }

    const renderBlockedView = () => (
        <button className={cn("btn-link", className)} disabled>
            <AddToPortfolioIcon />
        </button>
    );

    const getMenuItems = () =>
        identifierList.map(({ ticker, label }) => (
            <ContextMenuItem
                onClick={() => {
                    setIsTooltipVisible(false);
                    handleAddToPortfolioClick(ticker);
                }}
                key={label}
            >
                {ticker} <span>({label})</span>
            </ContextMenuItem>
        ));

    const renderTooltip = () => {
        const isAlreadyAdded = !!portfolioSecurity;
        return (
            <Tooltip
                overlayClassName={cn(
                    "kp-popover add-portfolio-popover",
                    className
                )}
                placement={TooltipPlacement.BottomRight}
                onVisibleChange={handleVisibilityChange}
                trigger={TooltipTriggerType.Click}
                visible={isTooltipVisible}
                overlay={() =>
                    isAlreadyAdded ? null : (
                        <div className="options-list">
                            <ul>{getMenuItems()}</ul>
                        </div>
                    )
                }
            >
                <span onMouseEnter={(e) => e.stopPropagation()}>
                    <OnHoverTooltip
                        overlay={
                            isAlreadyAdded
                                ? "Added to Portfolio"
                                : "Add to Portfolio"
                        }
                        disabled={isTooltipVisible}
                    >
                        <button
                            className="btn-link"
                            onClick={
                                isAlreadyAdded
                                    ? () => handleAddToPortfolioClick()
                                    : undefined
                            }
                        >
                            {isAlreadyAdded ? (
                                <IconSVG
                                    name="added-to-portfolio"
                                    width={16}
                                    height={16}
                                />
                            ) : (
                                <AddToPortfolioIcon />
                            )}
                            {withText &&
                                (isAlreadyAdded ? (
                                    <span>Added to Portfolio</span>
                                ) : (
                                    <span>Add to Portfolio</span>
                                ))}
                        </button>
                    </OnHoverTooltip>
                </span>
            </Tooltip>
        );
    };


    return (
        <ImSubscriptionActionBlocker
            overrideRequiredFeatures={!checkSubscription}
        >
            {blocked => (
                <div className={wrapperClassName}>
                    {blocked ? (
                        renderBlockedView()
                    ) : (
                        <>{renderTooltip()}</>
                    )}
                </div>
            )}
        </ImSubscriptionActionBlocker>
    );
}
